/**
 * Annotations-Overlay (Spec Kap. 5 Punkt 6): Agent-Pointer als DOM-Element und Zeichnungen auf
 * einem Canvas, beides in der Overlay-Ebene der SDK-Oberfläche (Shadow DOM, für rrweb
 * unsichtbar). Koordinaten kommen normiert (0..1 relativ zum Viewport) und werden mit
 * `window.innerWidth/innerHeight` multipliziert.
 */

/** Pointer verschwindet so lange nach dem letzten Update. */
export const POINTER_TIMEOUT_MS = 2_000;
/** Zeichnungen bleiben so lange voll sichtbar … */
export const DRAW_HOLD_MS = 4_000;
/** … und blenden dann so lange aus. */
export const DRAW_FADE_MS = 1_000;
/** Scrollen um mehr als diese Strecke (seit der Zeichnung) löscht sie. */
export const SCROLL_CLEAR_PX = 50;

const DEFAULT_COLOR = '#dc2626';
const LINE_WIDTH = 3;
const ARROW_HEAD_PX = 14;

export type DrawShape = 'arrow' | 'circle' | 'path';
type Point = readonly [number, number];

interface Drawing {
  shape: DrawShape;
  points: Point[];
  color: string;
  at: number;
  scrollX: number;
  scrollY: number;
}

/** Normierte Koordinate absichern: nicht-endliche Werte verwerfen, Rest auf 0..1 begrenzen. */
function norm(v: unknown): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  return Math.min(1, Math.max(0, v));
}

function toPoints(raw: unknown): Point[] {
  if (!Array.isArray(raw)) return [];
  const out: Point[] = [];
  for (const p of raw) {
    if (!Array.isArray(p)) continue;
    const x = norm(p[0]);
    const y = norm(p[1]);
    if (x !== null && y !== null) out.push([x, y]);
  }
  return out;
}

export class Annotations {
  private pointerEl: HTMLElement | null = null;
  private pointerTimer: ReturnType<typeof setTimeout> | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private drawings: Drawing[] = [];
  private phaseTimer: ReturnType<typeof setTimeout> | null = null;
  private raf: number | null = null;
  private listening = false;

  /** `layer` liefert die Overlay-Ebene (legt sie bei Bedarf an). */
  constructor(private readonly layer: () => HTMLElement) {}

  /** Anzahl sichtbarer Zeichnungen (für Tests). */
  get drawingCount(): number {
    return this.drawings.length;
  }

  // ---------------------------------------------------------------- Pointer

  pointer(x: unknown, y: unknown, label: unknown): void {
    const nx = norm(x);
    const ny = norm(y);
    if (nx === null || ny === null) return;
    let el = this.pointerEl;
    const layer = this.layer();
    if (!el) {
      el = document.createElement('div');
      el.className = 'relyo-pointer';
      el.setAttribute('data-relyo', 'pointer');
      el.setAttribute('aria-hidden', 'true');
      const text = document.createElement('span');
      text.className = 'relyo-pointer-label';
      text.setAttribute('data-relyo', 'pointer-label');
      el.append(text);
      this.pointerEl = el;
    }
    if (el.parentNode !== layer) layer.append(el);
    // Mittelpunkt des Punktes = Zielkoordinate (Versatz −7 px per CSS-Margin).
    el.style.left = `${nx * window.innerWidth}px`;
    el.style.top = `${ny * window.innerHeight}px`;
    const text = el.firstElementChild;
    const name = typeof label === 'string' ? label.slice(0, 64) : '';
    if (text && text.textContent !== name) text.textContent = name;
    if (this.pointerTimer !== null) clearTimeout(this.pointerTimer);
    this.pointerTimer = setTimeout(() => this.hidePointer(), POINTER_TIMEOUT_MS);
  }

  hidePointer(): void {
    if (this.pointerTimer !== null) clearTimeout(this.pointerTimer);
    this.pointerTimer = null;
    this.pointerEl?.remove();
    this.pointerEl = null;
  }

  // ------------------------------------------------------------ Zeichnungen

  draw(shape: unknown, rawPoints: unknown, color: unknown): void {
    if (shape !== 'arrow' && shape !== 'circle' && shape !== 'path') return;
    const points = toPoints(rawPoints);
    if (points.length === 0) return;
    this.drawings.push({
      shape,
      points,
      color: typeof color === 'string' && color ? color.slice(0, 32) : DEFAULT_COLOR,
      at: Date.now(),
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    });
    this.ensureCanvas();
    this.listen(true);
    this.render();
  }

  /** `annot.clear`: Zeichnungen und Pointer sofort entfernen. */
  clear(): void {
    this.clearDrawings();
    this.hidePointer();
  }

  /** Alle Zeichnungen sofort entfernen (Scroll, Resize, Navigation). */
  clearDrawings(): void {
    this.drawings = [];
    this.render();
  }

  /** Alles abbauen (Session-Ende). */
  destroy(): void {
    this.clear();
    this.canvas?.remove();
    this.canvas = null;
    this.ctx = null;
  }

  // ----------------------------------------------------------------- intern

  private ensureCanvas(): void {
    const layer = this.layer();
    if (!this.canvas) {
      const c = document.createElement('canvas');
      c.className = 'relyo-canvas';
      c.setAttribute('data-relyo', 'annot-canvas');
      c.setAttribute('aria-hidden', 'true');
      this.canvas = c;
      this.ctx = null;
    }
    if (this.canvas.parentNode !== layer) layer.prepend(this.canvas);
    this.sizeCanvas();
  }

  /** Canvas auf Viewport × devicePixelRatio skalieren (scharf auf HiDPI). */
  private sizeCanvas(): void {
    const c = this.canvas;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const pw = Math.max(1, Math.round(w * dpr));
    const ph = Math.max(1, Math.round(h * dpr));
    if (c.width !== pw) c.width = pw;
    if (c.height !== ph) c.height = ph;
    c.style.width = `${w}px`;
    c.style.height = `${h}px`;
    if (!this.ctx) {
      try {
        this.ctx = c.getContext('2d');
      } catch {
        this.ctx = null;
      }
    }
    // Größenänderung setzt den Kontext zurück; Transform jedes Mal neu setzen.
    this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private readonly onScroll = (): void => {
    const x = window.scrollX;
    const y = window.scrollY;
    const before = this.drawings.length;
    this.drawings = this.drawings.filter(
      (d) =>
        Math.abs(x - d.scrollX) <= SCROLL_CLEAR_PX && Math.abs(y - d.scrollY) <= SCROLL_CLEAR_PX,
    );
    if (this.drawings.length !== before) this.render();
  };

  private readonly onResize = (): void => {
    this.drawings = [];
    this.sizeCanvas();
    this.render();
  };

  private readonly onPageHide = (): void => this.clear();

  private listen(on: boolean): void {
    if (on === this.listening) return;
    this.listening = on;
    if (on) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      window.addEventListener('pagehide', this.onPageHide);
    } else {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('pagehide', this.onPageHide);
    }
  }

  /**
   * Zeichnet alle aktuellen Zeichnungen und plant den nächsten Schritt: Während der Haltephase
   * genügt ein Timer bis zum Beginn des Ausblendens; nur beim Ausblenden läuft
   * requestAnimationFrame. Ein Timer zum Ende des Ausblendens entfernt Zeichnungen auch dann,
   * wenn der Tab im Hintergrund keine Frames liefert.
   */
  private render(): void {
    const now = Date.now();
    const life = DRAW_HOLD_MS + DRAW_FADE_MS;
    this.drawings = this.drawings.filter((d) => now - d.at < life);
    this.paint(now);
    this.stopSchedule();
    if (this.drawings.length === 0) {
      this.listen(false);
      return;
    }
    let next = Infinity;
    let fading = false;
    for (const d of this.drawings) {
      const age = now - d.at;
      if (age < DRAW_HOLD_MS) next = Math.min(next, DRAW_HOLD_MS - age);
      else {
        fading = true;
        next = Math.min(next, life - age);
      }
    }
    this.phaseTimer = setTimeout(
      () => {
        this.phaseTimer = null;
        this.render();
      },
      Math.max(0, next),
    );
    if (fading && typeof requestAnimationFrame === 'function') {
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.render();
      });
    }
  }

  private stopSchedule(): void {
    if (this.phaseTimer !== null) clearTimeout(this.phaseTimer);
    this.phaseTimer = null;
    if (this.raf !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.raf);
    }
    this.raf = null;
  }

  private paint(now: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    ctx.clearRect(0, 0, w, h);
    for (const d of this.drawings) {
      const age = now - d.at;
      ctx.globalAlpha =
        age <= DRAW_HOLD_MS ? 1 : Math.max(0, 1 - (age - DRAW_HOLD_MS) / DRAW_FADE_MS);
      ctx.strokeStyle = DEFAULT_COLOR;
      ctx.strokeStyle = d.color; // ungültige Farben ignoriert der Canvas, Rot bleibt
      ctx.fillStyle = ctx.strokeStyle;
      ctx.lineWidth = LINE_WIDTH;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      strokeShape(
        ctx,
        d.shape,
        d.points.map(([x, y]) => [x * w, y * h] as const),
      );
    }
    ctx.globalAlpha = 1;
  }
}

function strokeShape(ctx: CanvasRenderingContext2D, shape: DrawShape, pts: Point[]): void {
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (!first || !last) return;
  ctx.beginPath();
  if (shape === 'arrow') {
    // Linie von points[0] nach points[last] mit Pfeilspitze am Ende.
    const [x0, y0] = first;
    const [x1, y1] = last;
    const angle = Math.atan2(y1 - y0, x1 - x0);
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    for (const side of [-1, 1]) {
      const a = angle + side * (Math.PI / 7);
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 - ARROW_HEAD_PX * Math.cos(a), y1 - ARROW_HEAD_PX * Math.sin(a));
    }
    ctx.stroke();
  } else if (shape === 'circle') {
    // Ellipse in der Bounding-Box aller Punkte.
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of pts) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    const rx = Math.max(1, (maxX - minX) / 2);
    const ry = Math.max(1, (maxY - minY) / 2);
    ctx.ellipse((minX + maxX) / 2, (minY + maxY) / 2, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (pts.length === 1) {
    // Freihand mit nur einem Punkt: kleiner Punkt.
    ctx.arc(first[0], first[1], LINE_WIDTH, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.moveTo(first[0], first[1]);
    for (const [x, y] of pts.slice(1)) ctx.lineTo(x, y);
    ctx.stroke();
  }
}
